import { useQuery, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { InboxListPage } from "../../../components/dashboard/inbox";
import { useOneTimePageAnimation } from "@/components/motion";
import {
  inboxSelectionFilter,
  parseInboxDirection,
  parseInboxSelection,
  serializeInboxSelection,
} from "@/features/inbox/filters";
import { verifiedIdentityOptionsQueryOptions } from "@/features/identities/queries";
import {
  flattenInboxThreadPages,
  inboxThreadsInfiniteQueryOptions,
} from "@/features/inbox/queries";

// How often the open list asks for new mail: close enough to feel live, far enough apart that an
// idle dashboard stays cheap.
const INBOX_REFRESH_INTERVAL_MS = 10_000;

export const Route = createFileRoute("/dashboard/inbox/")({
  validateSearch: (search: Record<string, unknown>) => {
    const includeBounce = search.includeBounce === true || search.includeBounce === "true";
    const includeWarmup = search.includeWarmup === true || search.includeWarmup === "true";

    return {
      domainId: (search.domainId as string) || undefined,
      aliasId: (search.aliasId as string) || undefined,
      direction: parseInboxDirection(search.direction),
      q: (search.q as string) || undefined,
      ...(includeBounce ? { includeBounce: true } : {}),
      ...(includeWarmup ? { includeWarmup: true } : {}),
    };
  },
  head: () => ({
    meta: [{ title: "Inbox - Bavimail" }],
  }),
  loaderDeps: ({ search }) => ({
    domainId: search.domainId,
    aliasId: search.aliasId,
    direction: search.direction,
    q: search.q,
    includeBounce: search.includeBounce,
    includeWarmup: search.includeWarmup,
  }),
  loader: async ({ deps, context }) => {
    // The inbox picker lists every verified alias; it should not pop in after the threads.
    void context.queryClient.prefetchQuery(verifiedIdentityOptionsQueryOptions());
    await context.queryClient.ensureInfiniteQueryData(
      inboxThreadsInfiniteQueryOptions({
        domainId: deps.domainId,
        ...inboxSelectionFilter(deps.aliasId),
        direction: deps.direction,
        search: deps.q,
        includeBounce: deps.includeBounce,
        includeWarmup: deps.includeWarmup,
      }),
    );
  },
  component: InboxRoute,
});

function InboxRoute() {
  const { domainId, aliasId, direction, q, includeBounce, includeWarmup } = Route.useSearch();
  const navigate = useNavigate();
  const threadAnimationStage = useOneTimePageAnimation("dashboard-inbox-threads");
  // Not suspended on: the list is useful before the picker knows its options.
  const identityOptions = useQuery(verifiedIdentityOptionsQueryOptions());
  const inboxes = useMemo(
    () =>
      (identityOptions.data?.aliases ?? [])
        .map((alias) => ({ id: alias.id, email: alias.fullEmail }))
        .sort((a, b) => a.email.localeCompare(b.email)),
    [identityOptions.data],
  );
  const threadsQuery = useSuspenseInfiniteQuery({
    ...inboxThreadsInfiniteQueryOptions({
      domainId,
      ...inboxSelectionFilter(aliasId),
      direction,
      search: q,
      includeBounce,
      includeWarmup,
    }),
    // New mail reaches the backend through the providers' pushes and the worker's sweeps; the
    // open list follows it without a manual refresh (paused while the tab is hidden).
    refetchInterval: INBOX_REFRESH_INTERVAL_MS,
  });

  const threads = useMemo(() => flattenInboxThreadPages(threadsQuery.data), [threadsQuery.data]);
  const lastPage = threadsQuery.data.pages[threadsQuery.data.pages.length - 1];

  return (
    <InboxListPage
      threads={threads}
      selectedDomainId={domainId}
      selectedAliasId={aliasId}
      inboxes={inboxes}
      selectedInboxIds={parseInboxSelection(aliasId)}
      onSelectInboxes={(ids) => {
        void navigate({
          to: "/dashboard/inbox",
          search: {
            domainId,
            aliasId: serializeInboxSelection(ids),
            direction,
            q,
            includeBounce,
            includeWarmup,
          },
        });
      }}
      activeDirection={direction}
      includeBounce={includeBounce}
      includeWarmup={includeWarmup}
      threadAnimationStage={threadAnimationStage}
      searchQuery={q || ""}
      hasMore={lastPage?.hasMore || false}
      onLoadMore={() => {
        if (threadsQuery.hasNextPage) {
          void threadsQuery.fetchNextPage();
        }
      }}
      isLoadingMore={threadsQuery.isFetchingNextPage}
      isSearching={threadsQuery.isFetching && !threadsQuery.isFetchingNextPage}
    />
  );
}
